import { getTreeStatusTotalsQuery } from '../../../services/api';
import type {
  Filter,
  Query,
  Totals as GqlTotals,
  TreeCategory,
  TreeViewType,
} from '../../../services/gql/types';
import { createGqlBatcher } from '../../../services/batchers/gqlBatcher';
import type { Totals as UiTotals } from '../../../types/types';

export type TreeStatusTotalsBatchContext = {
  gqlEndpoint: string;
  accessToken: string;
  category: TreeCategory;
  view: TreeViewType;
  daysBack: number;
  filters: Filter[];
};

export type TreeStatusTotalsBatchItem = {
  requestId: string;
  assetId?: string;
  path?: string;
};

export function gqlTotalsToUiTotals(gqlTotals: GqlTotals): UiTotals {
  return {
    passed: gqlTotals.passed,
    warnings: gqlTotals.warnings,
    failures: gqlTotals.failures,
    errors: gqlTotals.errors,
    noData: 0,
  };
}

function batchKey(ctx: TreeStatusTotalsBatchContext): string {
  return JSON.stringify({
    gqlEndpoint: ctx.gqlEndpoint,
    accessToken: ctx.accessToken,
    category: ctx.category,
    view: ctx.view,
    daysBack: ctx.daysBack,
    filters: ctx.filters,
  });
}

const { enqueue: enqueueTreeStatusTotalsBatch } = createGqlBatcher<
  TreeStatusTotalsBatchContext,
  TreeStatusTotalsBatchItem,
  Pick<Query, 'treeStatusTotals'>,
  UiTotals
>({
  windowMs: 150,
  maxBatch: 50,
  query: getTreeStatusTotalsQuery,
  batchKey,
  endpoint: (ctx) => ctx.gqlEndpoint,
  authToken: (ctx) => ctx.accessToken,
  itemRequestId: (item) => item.requestId,
  buildVariables: (ctx, items) => ({
    category: ctx.category,
    view: ctx.view,
    daysBack: ctx.daysBack,
    items: items.map((item) => ({
      requestId: item.requestId,
      assetId: item.assetId ?? null,
      path: item.path ?? null,
    })),
    filters: ctx.filters.length ? ctx.filters : undefined,
    excludeResourceTypes: ['exposure'],
  }),
  parseResponse: (data) =>
    new Map(
      data.treeStatusTotals.map((row) => [
        row.requestId,
        gqlTotalsToUiTotals(row.totals),
      ]),
    ),
});

export { enqueueTreeStatusTotalsBatch };
